import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, CheckCircle, Eye } from "lucide-react";

const CustomersQuery = () => {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [message, setMessage] = useState("");
  const chatEndRef = useRef(null);

  const queries = [
    {
      id: "QRY-001",
      customer: "Vishal Chaubey",
      message: "Product damage received, need replacement.",
      date: "13 Nov 2025",
      status: "Pending",
      chat: [
        { from: "customer", text: "Hello, my product is damaged." },
        { from: "admin", text: "Sorry! We will replace it." },
      ],
    },
    {
      id: "QRY-002",
      customer: "Subh",
      message: "Wrong size delivered, what is return process?",
      date: "14 Nov 2025",
      status: "Resolved",
      chat: [
        { from: "customer", text: "I got the wrong size." },
        { from: "admin", text: "You can return within 7 days." },
      ],
    },
    {
      id: "QRY-003",
      customer: "Chaubey",
      message: "Payment deducted but order not received.",
      date: "15 Nov 2025",
      status: "Pending",
      chat: [
        { from: "customer", text: "Payment done, order not received." },
        { from: "admin", text: "We are checking it." },
      ],
    },
  ];

  // Scroll to bottom on new message or customer change
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedCustomer, message]);

  const handleSend = () => {
    if (message.trim() && selectedCustomer) {
      selectedCustomer.chat.push({ from: "admin", text: message });
      setMessage("");
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Left Sidebar - Chat List */}
      <div className="w-80 bg-white border-r overflow-y-auto">
        <h1 className="text-2xl font-bold p-4 flex items-center gap-2">
          <MessageCircle size={24} /> Customers
        </h1>
        {queries.map((q) => (
          <div
            key={q.id}
            onClick={() => setSelectedCustomer(q)}
            className={`cursor-pointer p-4 border-b hover:bg-gray-100 transition-colors ${
              selectedCustomer?.id === q.id ? "bg-gray-200" : ""
            }`}
          >
            <div className="flex justify-between items-center">
              <h2 className="font-medium">{q.customer}</h2>
              <span
                className={`px-2 py-1 rounded-full text-xs font-semibold ${
                  q.status === "Resolved"
                    ? "bg-green-100 text-green-700"
                    : "bg-yellow-100 text-yellow-700"
                }`}
              >
                {q.status}
              </span>
            </div>
            <p className="text-gray-600 text-sm truncate">{q.message}</p>
            <p className="text-gray-400 text-xs mt-1">{q.date}</p>
          </div>
        ))}
      </div>

      {/* Right Panel - Chat Window */}
      <div className="flex-1 flex flex-col relative">
        {selectedCustomer ? (
          <>
            <div className="border-b pb-3 mb-3 flex justify-between items-center px-4">
              <h2 className="text-xl font-bold">{selectedCustomer.customer}</h2>
              <div className="flex gap-2">
                <button className="bg-blue-600 text-white px-3 py-1 rounded-lg flex items-center gap-1">
                  <Eye size={16} /> View
                </button>
                {selectedCustomer.status !== "Resolved" && (
                  <button className="bg-green-600 text-white px-3 py-1 rounded-lg flex items-center gap-1">
                    <CheckCircle size={16} /> Resolve
                  </button>
                )}
              </div>
            </div>

            {/* Chat Messages - scrollable */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 flex flex-col">
              {selectedCustomer.chat.map((msg, idx) => (
                <div
                  key={idx}
                  className={`max-w-xs p-3 rounded-lg ${
                    msg.from === "admin"
                      ? "bg-blue-600 text-white self-end"
                      : "bg-white text-gray-800 self-start"
                  }`}
                >
                  {msg.text}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            {/* Input Box - fixed at bottom */}
            <div className="p-4 border-t flex gap-2 bg-gray-100">
              <input
                type="text"
                placeholder="Type a message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                className="flex-1 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button
                onClick={handleSend}
                className="bg-blue-600 text-white px-4 py-3 rounded-lg"
              >
                Send
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex justify-center items-center text-gray-400">
            Select a customer to start chat
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomersQuery;
